import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Database, ExternalLink, Settings2, ShieldCheck, HelpCircle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const WordpressGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="flex items-center gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm shrink-0">
              <Database className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Petunjuk Database WordPress</h1>
              <p className="text-sm md:text-lg text-muted-foreground mt-1">
                Panduan langkah demi langkah untuk menghubungkan database WordPress Anda secara aman.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Settings2 className="h-6 w-6 text-primary" />
              1. Detail Database WordPress
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <p className="text-muted-foreground">
                  Cari file bernama <strong className="text-foreground">wp-config.php</strong> di folder utama WordPress. Cari baris-baris berikut:
                </p>
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-md font-mono text-[11px] overflow-x-auto border-l-4 border-primary shadow-inner max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`define( 'DB_NAME', 'nama_db' );
define( 'DB_USER', 'user_utama' );
define( 'DB_PASSWORD', 'password_anda' );
define( 'DB_HOST', 'localhost' );`}
                </pre>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <ShieldCheck className="h-6 w-6 text-primary" />
              2. Buat User "Read-Only"
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <p className="text-muted-foreground">
                  Gunakan user khusus yang hanya bisa membaca data (SELECT) untuk keamanan maksimal:
                </p>
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-md font-mono text-[11px] overflow-x-auto border-l-4 border-primary shadow-inner max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`CREATE USER 'chatbaze_user'@'%' 
IDENTIFIED BY 'password_anda';

GRANT SELECT ON nama_db.* 
TO 'chatbaze_user'@'%';

FLUSH PRIVILEGES;`}
                </pre>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-3 text-amber-600 text-[10px] italic">
                  <strong>Catatan:</strong> Ganti % dengan IP server Chatbaze jika Anda ingin lebih aman.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <ExternalLink className="h-6 w-6 text-primary" />
              3. Remote MySQL (cPanel)
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base leading-relaxed text-muted-foreground">
                <p>Jika WordPress Anda ada di hosting (cPanel), biasanya koneksi dari luar diblokir.</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Login ke <strong className="text-foreground">cPanel</strong>.</li>
                  <li>Cari menu <strong className="text-foreground">Remote MySQL</strong>.</li>
                  <li>Tambahkan tanda <strong className="text-foreground">%</strong> atau masukkan IP server Chatbaze.</li>
                  <li>Klik <strong className="text-foreground">Add Host</strong>.</li>
                </ol>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Database className="h-6 w-6 text-primary" />
              4. Mengisi Form Chatbaze
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <span className="font-bold text-foreground">Nama Connector:</span>
                    <span className="text-muted-foreground italic text-right">WordPress DB</span>
                    
                    <span className="font-bold text-foreground">Host:</span>
                    <span className="text-muted-foreground text-right">IP Server Website</span>
                    
                    <span className="font-bold text-foreground">Port:</span>
                    <span className="text-muted-foreground text-right">3306 (Default)</span>
                    
                    <span className="font-bold text-foreground">DB Name:</span>
                    <span className="text-muted-foreground text-right">DB_NAME</span>
                    
                    <span className="font-bold text-foreground">User:</span>
                    <span className="text-muted-foreground text-right">chatbaze_user</span>
                  </div>
                  <div className="bg-primary/5 rounded-lg p-3 border border-primary/10">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      <strong className="text-primary">Tips:</strong> Gunakan tabel <code>wp_posts</code> dengan filter <code>post_status = 'publish'</code> agar chatbot hanya membaca artikel aktif.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold text-foreground">Filter Konten Chatbot</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-primary/10 shadow-sm">
              <CardHeader className="bg-primary/5 py-3">
                <CardTitle className="text-base flex items-center gap-2 text-foreground">
                  <Settings2 className="h-4 w-4 text-primary" />
                  Cara 1: System Prompt
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <p className="text-xs text-muted-foreground">AI akan menulis query SQL sendiri berdasarkan instruksi Anda di <strong>AI Settings</strong>.</p>
                <div className="bg-muted p-3 rounded-md text-[11px] font-mono border italic">
                  "Gunakan tabel wp_posts. Filter post_status = 'publish' dan post_type = 'post'."
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/10 shadow-sm">
              <CardHeader className="bg-primary/5 py-3">
                <CardTitle className="text-base flex items-center gap-2 text-foreground">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Cara 2: MySQL View
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <p className="text-xs text-muted-foreground">AI hanya bisa melihat data yang Anda ekspos melalui View di database.</p>
                <pre className="bg-slate-900 text-slate-100 p-2 rounded text-[10px] overflow-x-auto border max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`CREATE VIEW wp_chatbot_data AS 
SELECT post_title, post_content 
FROM wp_posts 
WHERE post_status = 'publish';`}
                </pre>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-primary text-primary-foreground rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <Globe className="h-7 w-7" />
            Cek IP Server Chatbaze.id
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div className="space-y-2">
              <p className="font-bold opacity-100">1. Ping (Cepat)</p>
              <code className="block bg-primary-foreground/10 p-2 rounded font-mono text-xs border border-primary-foreground/20">ping api.chatbaze.id</code>
            </div>
            <div className="space-y-2">
              <p className="font-bold opacity-100">2. Curl (Akurat)</p>
              <code className="block bg-primary-foreground/10 p-2 rounded font-mono text-xs border border-primary-foreground/20">curl -s ifconfig.me</code>
            </div>
            <div className="space-y-2">
              <p className="font-bold opacity-100">3. VPS Panel</p>
              <p className="text-xs opacity-90">Lihat IP Publik IPv4 di dashboard DigitalOcean, AWS, dll.</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-primary-foreground/20 text-center opacity-90">
            <p className="text-xs italic">
              <strong>Saran:</strong> Gunakan Cara 2 (Curl) untuk mendapatkan IP pasti yang akan mengakses database Anda.
            </p>
          </div>
        </div>

        <div className="pt-8 flex flex-col items-center gap-4">
          <p className="text-xs text-muted-foreground">
            Butuh bantuan teknis? Hubungi tim support kami melalui chat di pojok kanan bawah.
          </p>
      </div>
    </div>
  );
};

export default WordpressGuide;
